import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { PersonDTO } from '../../api/api';
import { CloseIcon } from '../../assets/icons/CloseIcon';
import { SendIcon } from '../../assets/icons/SendIcon';
import NotificationService from '../../services/NotificationService';
import TaskService from '../../services/TaskService';
import { log } from '../../utils/logging/log';
import { DialogFooter, DialogHeader } from '../../views/goods-view/components/Styles';
import Button from '../button/Button';
import CircularProgress from '../circular-progress/CircularProgress';
import DialogView from '../dialog-view/DialogView';
import IconButton from '../icon-button/IconButton';
import RadioButtons from '../radio-buttons/RadioButtons';
import Typography from '../typography/Typography';

interface Props {
  open: boolean;
  workTaskId?: number;
  handleClose: () => void;
  onTransferred: () => void;
}

// Only "første mand" can close a task, so handing that role to one of the other technicians booked on
// it is how a technician leaves a task that is not finished yet.
const TransferResponsibilityDialog = (props: Props) => {
  const { open, workTaskId, handleClose, onTransferred } = props;
  const [technicians, setTechnicians] = useState<PersonDTO[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    if (!open || !workTaskId) return;
    setIsLoading(true);
    setSelectedEmail(undefined);
    TaskService.getBookedTechnicians(workTaskId)
      .then((res) => setTechnicians(res))
      .catch((err) => {
        log(err);
        NotificationService.error('Teknikerne på opgaven kunne ikke hentes. Prøv igen.');
      })
      .finally(() => setIsLoading(false));
  }, [open, workTaskId]);

  const handleTransfer = useCallback(() => {
    const newTechnician = technicians.find((t) => t.email === selectedEmail);
    if (!workTaskId || !newTechnician) return;

    setIsTransferring(true);
    TaskService.transferResponsibility(workTaskId, newTechnician)
      .then(() => {
        NotificationService.success(`${newTechnician.name} er nu første mand på opgaven`);
        onTransferred();
        handleClose();
      })
      .catch((err) => {
        log(err);
        NotificationService.error('Ansvaret kunne ikke overdrages. Prøv igen.');
      })
      .finally(() => setIsTransferring(false));
  }, [technicians, selectedEmail, workTaskId, onTransferred, handleClose]);

  if (!open) return null;

  return (
    <DialogView handleClose={handleClose}>
      <Container>
        <StyledDialogHeader>
          <Typography variant="h4" fontWeight="bold">
            Overdrag ansvar
          </Typography>
          <IconButton variant="outlined" onClick={handleClose}>
            <CloseIcon size="18px" />
          </IconButton>
        </StyledDialogHeader>

        <Content>
          {isLoading && <CircularProgress />}
          {!isLoading && technicians.length === 0 && (
            <Typography>Der er ingen andre teknikere med bookinger på opgaven.</Typography>
          )}
          {!isLoading && technicians.length > 0 && (
            <RadioButtons
              label="Vælg hvem der skal være første mand"
              value={selectedEmail}
              handleValueChange={(value: string) => setSelectedEmail(value)}
              labelPlacement="end"
              required
              options={technicians.map((technician) => ({
                label: technician.name ?? technician.email ?? '',
                value: technician.email
              }))}
            />
          )}
        </Content>

        <DialogFooter>
          <Buttons>
            <Button variant="secondary" onClick={handleClose}>
              Fortryd
            </Button>
            <Button
              onClick={handleTransfer}
              disabled={!selectedEmail}
              isLoading={isTransferring}
              data-testid="transfer-responsibility-confirm"
            >
              <SendIcon size="20px" />
              Overdrag ansvar
            </Button>
          </Buttons>
        </DialogFooter>
      </Container>
    </DialogView>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  width: 85vw;
  max-width: 520px;
  box-sizing: border-box;
`;

const StyledDialogHeader = styled(DialogHeader)`
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  min-height: 80px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 12px;
  width: 100%;
`;

export default TransferResponsibilityDialog;
